import React, { useEffect, useState, useRef } from "react";
import { CaretDownFilled, SaveFilled, ExportOutlined, SettingOutlined } from "@ant-design/icons";
import { faSearch, faFilter, faDownload, faList, faTrashCanArrowUp, faBoxOpen, faFileImport } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Badge, Button, Dropdown, Input, Menu, Popover } from "antd";
import { useDispatch } from "react-redux";
import { resetAdvanceFilter, resetQuickFilter, setQuickFilter } from "@src/middleware/redux/reducers/quickFilter";
import { useSelector } from "react-redux";
import { setEditGridColumn } from "@src/middleware/redux/reducers/properties.reducer";
import { setTriggerExport } from "@src/middleware/redux/reducers/tableSelection.reducer";
import { createdDateList } from "@src/util/date";
import { setNotification } from "@src/middleware/redux/reducers/notification.reducer";
import "./gridFilter.css";
import { setTogglenewCreateView } from "@src/middleware/redux/reducers/newView.reducer";
import { useDebounce } from "../../table";
import MenuItem from "antd/es/menu/MenuItem";
import { Download, List } from "lucide-react";

// onImport: a module that can bring records in from a file passes what opens its import, and
// Table Actions gets an Import option
// toolbarExtra: a module's own controls (e.g. the timeline's date bar), placed just before Table
// Actions; to make room the search box can shrink, Clear All only shows while there's a filter,
// and on a narrow screen the row wraps
export const GridFilter = ({openAdvanceFilter, updateView, refetch, viewList, firstFilter="Create date", secondFilter="Last activity date", quickFilterDisabled=false, viewMode, onViewModeChange, setSearch, title, setPage, setPageSize, showArchived, onToggleShowArchived, onImport, toolbarExtra})=>{
    const [createdDate, setCreatedDate] = useState([...createdDateList]);
    const [createdDateSearch, setCreatedDateSearch] = useState();
    const [createdDatePop, setCreatedDatePop] = useState();
    const [createdDateFilter, setCreatedDateFilter] = useState();

    const [activityProp, setactivityProp] = useState(false);
    const [activityDateFilter, setActivityDateFilter] = useState();
    const [activityDateList, setActivityDateList] = useState([...createdDateList]);
    const [activityDateSearch, setActivityDateSearch] = useState();
    
    // get id of user who created this view to check it is allowed to edit or not

    const [viewCreatedBy, setViewCreatedBy] = useState(null);
    
    useEffect(()=>{
        if(viewList?.length>0){
            const viewDetail = viewList.find((view)=>view?._id==sessionStorage.getItem('selectedViewId'));
            if(viewDetail?.createdBy){
                setViewCreatedBy(viewDetail?.createdBy);
            }else{
                setViewCreatedBy(null)
            }
        }
    },[sessionStorage.getItem('selectedViewId'), viewList]);


    const popoverRef = useRef(null);
    const inputRef = useRef(null);
    const dispatch = useDispatch();



    const {quickFilter, advanceFilter} = useSelector(state=>state.quickFilterReducer);

    const { refetchBranchView } = useSelector(state => state.branchViewReducer);

    const [loading, setLoading] = useState(false);
    const {authenticatedUserDetail} = useSelector(state=>state.userAuthReducer);

    const handelSaveView = async () =>{
        setLoading(true);
        await updateView({
            variables:{
                input:{
                    _id: sessionStorage.getItem('selectedViewId'),
                    quickFilter,
                    advanceFilter,
                    updatedBy: authenticatedUserDetail?._id
                }
            }
        });
        dispatch(setNotification({
            notificationState:true, 
            message:"View saved successfully",
            error: false,
        }));
        await refetch();
        // await refetchBranchView();
        setLoading(false);
    };
    
    const advanceFilterNumber = advanceFilter.reduce((accumulator, currentArray) => {
        return accumulator + currentArray.length;
    }, 0)

    const [openSaveView, setOpenSaveView] = useState(false);
    const {togglenewCreateView} = useSelector(state=>state.newViewReducer)

    useEffect(()=>{
        setOpenSaveView(false);
    },[togglenewCreateView]);


    const [localSearch, setLocalSearch] = useState("");
    const debouncedSearch = useDebounce(localSearch, 500); // 500ms delay
  
    const hasMountedRef = useRef(false);
    const restoredSearchRef = useRef('');
    const storageKey = `grid_state_${title || location.pathname}`;

    // Trigger search — reset page to 1 on user-driven search changes.
    // Initial mount run and restored-search run skip the page reset.
    useEffect(() => {
      if (!hasMountedRef.current) {
        hasMountedRef.current = true;
        setSearch(debouncedSearch);
        return;
      }
      if (restoredSearchRef.current !== '' && debouncedSearch === restoredSearchRef.current) {
        restoredSearchRef.current = '';
        setSearch(debouncedSearch);
        return;
      }
      setSearch(debouncedSearch);
      setPage(1);
    }, [debouncedSearch]);
  

    // Restore pagination state when returning from a detail page.
    // Uses setTimeout to defer after all parent components' initial effects have run
    // (parent effects run after child effects, and they call setPage(1) on [search] mount).
    useEffect(() => {
        const saved = sessionStorage.getItem(storageKey);
        if (!saved) return;
        sessionStorage.removeItem(storageKey);
        let timer;
        try {
        const { page, size, searchVal } = JSON.parse(saved);
        timer = setTimeout(() => {
            if (size) setPageSize(size);
            if (page) setPage(page);
            if (searchVal !== undefined) {
            restoredSearchRef.current = searchVal;
            setLocalSearch(searchVal);
            }
        }, 0);
        } catch (e) { /* ignore corrupt storage */ }
        return () => clearTimeout(timer);
    }, []);


    const { isExporting } = useSelector(state => state.tableSelectionReducer);

    const menu = (
        <Menu>
            {onImport && (
                <MenuItem onClick={onImport} data-import-action>
                    <FontAwesomeIcon icon={faFileImport} style={{marginRight:'6px'}}/> Import
                </MenuItem>
            )}
            <MenuItem onClick={()=> dispatch(setTriggerExport(true))}>
                <FontAwesomeIcon icon={faDownload} style={{marginRight:'6px'}}/>{isExporting ? 'Exporting...' : 'Export'}
            </MenuItem>
            <MenuItem onClick={()=> dispatch(setEditGridColumn(true))}>
                <FontAwesomeIcon icon={faList} style={{marginRight:'6px'}}/> Edit Columns
            </MenuItem>
            {onToggleShowArchived && (
                <MenuItem onClick={()=> onToggleShowArchived(!showArchived)}>
                    <FontAwesomeIcon icon={showArchived ? faTrashCanArrowUp : faBoxOpen} style={{marginRight:'6px'}}/>
                    {showArchived ? `Back to active ${title}s` : `Show archived ${title}s`}
                </MenuItem>
            )}
        </Menu>
    );

    return(
        <div className='grid-head-section' style={toolbarExtra ? {paddingTop:'0px', paddingBottom:'0px', flexWrap:'wrap', rowGap:'10px', columnGap:'24px'} : {paddingTop:'0px', paddingBottom:'0px'}}>
                <div className='grid-head-left-btn-section' style={toolbarExtra ? {flex: '1 1 320px', minWidth: 0, columnGap: '24px'} : {flex: 1}}>
                        <Input type='search' allowClear value={localSearch} onChange={(e)=>setLocalSearch(e.target.value)} style={{background: 'white', width:'min(500px, 100%)', maxWidth:'500px', borderRadius:'15px'}} className='generic-input-control' placeholder={`Search ${title}...`} suffix={<FontAwesomeIcon style={{color:'#0091ae'}} icon={faSearch}/>}/>
                     

                    <span onClick={openAdvanceFilter}>
                        <FontAwesomeIcon color='#0091ae' icon={faFilter} />&nbsp;<span className='grid-text-btn' style={toolbarExtra ? {whiteSpace:'nowrap'} : undefined}>  Filters ({advanceFilterNumber}) </span>
                    </span>
                    {(!toolbarExtra || advanceFilterNumber > 0) &&
                    <span type='text' className='grid-text-btn' style={{whiteSpace:'nowrap'}}
                        onClick={()=>{
                            dispatch(resetAdvanceFilter());
                            dispatch(resetQuickFilter());
                        }}
                    > Clear All </span>
                    }

                </div>

                <div style={toolbarExtra ? {display:'flex', alignItems:'center', gap:'10px', flexWrap:'wrap', marginLeft:'auto'} : {display:'flex', alignItems:'center', gap:'10px'}}>
                    {toolbarExtra}
                    <Dropdown overlay={menu}>
                        <Badge dot={!!showArchived} offset={[-8, 6]}>
                            <button className="sm-btn" style={{height:'40px', width:'150px'}}>Table Actions <CaretDownFilled/> </button>
                        </Badge>
                    </Dropdown>
                </div>

            </div>
    )
}